import React from 'react';
import type { SkyWrenchReferenceAccess } from '../services/skyWrenchService';

interface SkyWrenchReferenceCardsProps {
  sourceUris: readonly string[];
  openingUri: string | null;
  error: string;
  onOpen: (sourceUri: string) => void;
}

interface SkyWrenchReferenceViewerProps {
  document: SkyWrenchReferenceAccess | null;
  onClose: () => void;
}

const ReferenceDocumentIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H11v16H6.5A2.5 2.5 0 0 0 4 21.5v-16Z" />
    <path d="M20 5.5A2.5 2.5 0 0 0 17.5 3H13v16h4.5a2.5 2.5 0 0 1 2.5 2.5v-16Z" />
  </svg>
);

/** Returns unique, valid-looking source URIs while preserving response order. */
export function uniqueSourceUris(sourceUris: readonly string[]): string[] {
  return Array.from(new Set(sourceUris.filter((uri: string): boolean => uri.startsWith('s3://'))));
}

/** Returns the PDF filename shown on a standalone-style reference card. */
export function getReferenceFilename(sourceUri: string): string {
  const encodedFilename: string = sourceUri.split('/').pop() || 'Maintenance reference.pdf';
  try {
    return decodeURIComponent(encodedFilename);
  } catch {
    return encodedFilename;
  }
}

/** Document cards matching the selectable reference treatment in SkyWrench. */
export const SkyWrenchReferenceCards: React.FC<SkyWrenchReferenceCardsProps> = ({
  sourceUris,
  openingUri,
  error,
  onOpen,
}) => {
  const references: string[] = uniqueSourceUris(sourceUris);
  if (references.length === 0) return null;

  return (
    <div className="skywrench-references">
      <div className="skywrench-reference-grid" aria-label="SkyWrench source documents">
        {references.map((sourceUri: string) => {
          const filename: string = getReferenceFilename(sourceUri);
          return (
            <button
              className="skywrench-reference-card"
              type="button"
              key={sourceUri}
              onClick={() => onOpen(sourceUri)}
              disabled={openingUri !== null}
              aria-label={`Open reference ${filename}`}
              title={`Open ${filename}`}
            >
              <span className="skywrench-reference-icon" aria-hidden="true">
                <ReferenceDocumentIcon />
              </span>
              <span className="skywrench-reference-name">{filename}</span>
            </button>
          );
        })}
      </div>
      {error && (
        <p className="skywrench-reference-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

/** Embedded PDF popup for a selected SkyWrench source document. */
export const SkyWrenchReferenceViewer: React.FC<SkyWrenchReferenceViewerProps> = ({
  document,
  onClose,
}) => {
  if (!document) return null;
  return (
    <div
      className="skywrench-reference-viewer-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={document.filename}
      onClick={onClose}
    >
      <div
        className="skywrench-reference-viewer"
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
      >
        <div className="skywrench-reference-viewer-header">
          <h3>{document.filename}</h3>
          <button type="button" onClick={onClose} aria-label="Close reference viewer">
            &times;
          </button>
        </div>
        <iframe
          className="skywrench-reference-frame"
          src={document.url}
          title={document.filename}
        />
      </div>
    </div>
  );
};
